import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Stack, Button, TextField,
    Divider, Chip, Avatar, IconButton, Card, CardContent,
    Tabs, Tab, CircularProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, Checkbox,
    List, ListItem, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CampaignIcon from '@mui/icons-material/Campaign';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const ClassroomDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [group, setGroup] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [newUpdate, setNewUpdate] = useState({ title: '', content: '' });
    const [attendanceState, setAttendanceState] = useState<any>({});
    const [achievementDialog, setAchievementDialog] = useState({ open: false, studentId: '', studentName: '' });
    const [achievementData, setAchievementData] = useState({ badgeType: 'STAR_STUDENT', comment: '' });
    const [manageStudentsDialog, setManageStudentsDialog] = useState(false);
    const [allStudents, setAllStudents] = useState<any[]>([]);
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    const [studentSearch, setStudentSearch] = useState('');
    const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
    const [newAssignment, setNewAssignment] = useState({ title: '', description: '', dueDate: '', attachmentUrl: '' });

    const userInfo = localStorage.getItem('userInfo');

    const fetchGroupDetails = useCallback(async () => {
        try {
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl(`groups/${id}`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroup(res.data);
        } catch (error) {
            console.error('Fetch group error:', error);
        } finally {
            setLoading(false);
        }
    }, [id, userInfo]);

    const fetchAttendanceForDate = useCallback(async () => {
        if (!id || !group?.students) return;
        try {
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl(`attendance-records?classGroupId=${id}&date=${selectedDate}`), {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Initialize default attendance (all present)
            const initialAttendance: any = {};
            group.students.forEach((s: any) => {
                initialAttendance[s.id] = 'PRESENT';
            });

            // Overwrite with existing records if found
            if (res.data && res.data.length > 0) {
                res.data.forEach((rec: any) => {
                    initialAttendance[rec.studentId] = rec.status;
                });
            }
            setAttendanceState(initialAttendance);
        } catch (error) {
            console.error('Fetch attendance error:', error);
        }
    }, [id, selectedDate, userInfo, group?.students]);

    const fetchAllStudents = useCallback(async () => {
        try {
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('students'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllStudents(res.data);
        } catch (error) {
            console.error('Fetch all students error:', error);
        }
    }, [userInfo]);

    useEffect(() => {
        fetchGroupDetails();
        fetchAllStudents();
    }, [fetchGroupDetails, fetchAllStudents]);

    useEffect(() => {
        if (tabValue === 2) {
            fetchAttendanceForDate();
        }
    }, [tabValue, selectedDate, fetchAttendanceForDate]);

    const handlePostUpdate = async () => {
        try {
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl(`groups/${id}/updates`), newUpdate, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNewUpdate({ title: '', content: '' });
            fetchGroupDetails();
        } catch (error) {
            console.error('Post update error:', error);
        }
    };

    const handleCreateAssignment = async () => {
        try {
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl(`groups/${id}/assignments`), newAssignment, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAssignmentDialogOpen(false);
            setNewAssignment({ title: '', description: '', dueDate: '', attachmentUrl: '' });
            fetchGroupDetails();
        } catch (error) {
            console.error('Create assignment error:', error);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
    if (!group) return <Typography sx={{ p: 4 }}>Classroom not found</Typography>;

    return (
        <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            {/* Header */}
            <Stack direction="row" spacing={2} alignItems="center" mb={4}>
                <IconButton onClick={() => navigate('/dashboard/groups')} sx={{ bgcolor: 'white' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" fontWeight={800} color="#263B7C">{group.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Teacher: {group.teacher.name} | Grade {group.grade} | <Chip label={group.category} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    sx={{ bgcolor: '#263B7C', borderRadius: '12px' }}
                    onClick={() => {
                        setSelectedStudentIds(group.students.map((s: any) => s.id));
                        setManageStudentsDialog(true);
                    }}
                >
                    Manage Students
                </Button>
            </Stack>

            <Grid container spacing={4}>
                {/* Main Content: Feed & Activity */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Tabs
                            value={tabValue}
                            onChange={(_, v) => setTabValue(v)}
                            sx={{ px: 3, pt: 2, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' } }}
                        >
                            <Tab label="Class Feed" icon={<CampaignIcon />} iconPosition="start" />
                            <Tab label="Assignments" icon={<AssignmentIcon />} iconPosition="start" />
                            <Tab label="Attendance Hub" icon={<CheckCircleIcon />} iconPosition="start" />
                        </Tabs>
                        <Divider />

                        <Box sx={{ p: 4 }}>
                            {tabValue === 0 && (
                                <Stack spacing={4}>
                                    {/* Create Update Box */}
                                    <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#fbfcfd' }}>
                                        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>📢 Post Class Update</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>Updates are shared with all parents in this class.</Typography>
                                        <TextField
                                            placeholder="Update Title"
                                            fullWidth
                                            sx={{ mb: 2 }}
                                            value={newUpdate.title}
                                            onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
                                        />
                                        <TextField
                                            placeholder="What's happening in class today?..."
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={newUpdate.content}
                                            onChange={(e) => setNewUpdate({ ...newUpdate, content: e.target.value })}
                                        />
                                        <Stack direction="row" justifyContent="flex-end" mt={2}>
                                            <Button variant="contained" onClick={handlePostUpdate} sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}>Post to Feed</Button>
                                        </Stack>
                                    </Paper>

                                    {/* Feed Items */}
                                    {group.updates.map((update: any) => (
                                        <Card key={update.id} elevation={0} sx={{ border: '1px solid #edf2f7', borderRadius: '16px' }}>
                                            <CardContent>
                                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                                    <Avatar sx={{ bgcolor: '#F9B43F', fontWeight: 700 }}>{group.teacher.name[0]}</Avatar>
                                                    <Box>
                                                        <Typography fontWeight={700}>{update.title}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(update.createdAt).toLocaleDateString()} at {new Date(update.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Typography variant="body2">{update.content}</Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            )}

                            {tabValue === 1 && (
                                <Stack spacing={3}>
                                    <Button 
                                        startIcon={<AddIcon />} 
                                        variant="outlined" 
                                        sx={{ borderRadius: '12px', borderStyle: 'dashed' }}
                                        onClick={() => setAssignmentDialogOpen(true)}
                                    >
                                        New Homework Assignment
                                    </Button>
                                    {group.assignments.map((task: any) => (
                                        <Paper key={task.id} sx={{ p: 3, borderRadius: '16px', border: '1px solid #edf2f7' }} elevation={0}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                <Box>
                                                    <Typography variant="h6" fontWeight={800}>{task.title}</Typography>
                                                    <Typography variant="body2" color="text.secondary" mb={2}>{task.description}</Typography>
                                                    <Chip label={`Due: ${new Date(task.dueDate).toLocaleDateString()}`} color="error" size="small" sx={{ fontWeight: 700 }} />
                                                </Box>
                                                {task.attachmentUrl && (
                                                    <IconButton color="primary" sx={{ bgcolor: '#EBF8FF' }}>
                                                        <AssignmentIcon />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}

                            {tabValue === 2 && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={800}>Daily Attendance</Typography>
                                            <TextField
                                                type="date"
                                                size="small"
                                                variant="standard"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                InputProps={{ disableUnderline: true, sx: { fontWeight: 600, color: 'text.secondary' } }}
                                                sx={{ mt: -0.5 }}
                                            />
                                        </Box>
                                        <Button
                                            variant="contained"
                                            startIcon={<CheckCircleIcon />}
                                            disabled={new Date(selectedDate) > new Date()}
                                            sx={{ bgcolor: '#263B7C', borderRadius: '12px', '&:disabled': { bgcolor: '#e0e0e0' } }}
                                            onClick={async () => {
                                                try {
                                                    const token = userInfo ? JSON.parse(userInfo).token : null;
                                                    await axios.post(getApiUrl(`groups/${id}/attendance`), {
                                                        date: selectedDate,
                                                        status: attendanceState
                                                    }, { headers: { Authorization: `Bearer ${token}` } });
                                                    alert('Attendance saved successfully!');
                                                } catch (error) {
                                                    console.error('Save attendance error:', error);
                                                    alert('Failed to save attendance');
                                                }
                                            }}
                                        >
                                            {new Date(selectedDate) > new Date() ? 'Cannot Mark Future' : 'Save Attendance'}
                                        </Button>
                                    </Stack>

                                    <Grid container spacing={2}>
                                        {group.students.map((student: any) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={student.id}>
                                                <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', border: attendanceState[student.id] === 'ABSENT' ? '2px solid #FF5252' : '1px solid #edf2f7' }}>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Avatar src={`https://i.pravatar.cc/150?u=${student.id}`} />
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography variant="subtitle2" fontWeight={700}>{student.firstName}</Typography>
                                                            <Stack direction="row" spacing={0.5}>
                                                                <Chip
                                                                    label="P"
                                                                    size="small"
                                                                    onClick={() => setAttendanceState({ ...attendanceState, [student.id]: 'PRESENT' })}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        bgcolor: attendanceState[student.id] === 'PRESENT' ? '#4CAF50' : '#f0f0f0',
                                                                        color: attendanceState[student.id] === 'PRESENT' ? 'white' : 'inherit'
                                                                    }}
                                                                />
                                                                <Chip
                                                                    label="A"
                                                                    size="small"
                                                                    onClick={() => setAttendanceState({ ...attendanceState, [student.id]: 'ABSENT' })}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        bgcolor: attendanceState[student.id] === 'ABSENT' ? '#FF5252' : '#f0f0f0',
                                                                        color: attendanceState[student.id] === 'ABSENT' ? 'white' : 'inherit'
                                                                    }}
                                                                />
                                                                <Chip
                                                                    label="L"
                                                                    size="small"
                                                                    onClick={() => setAttendanceState({ ...attendanceState, [student.id]: 'LATE' })}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        bgcolor: attendanceState[student.id] === 'LATE' ? '#FFC107' : '#f0f0f0',
                                                                        color: attendanceState[student.id] === 'LATE' ? 'white' : 'inherit'
                                                                    }}
                                                                />
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Sidebar: Roster & Highlights */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={4}>
                        <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                                <Typography variant="h6" fontWeight={800}>Student Roster</Typography>
                                <Chip label={`${group.students.length} Pupils`} size="small" />
                            </Stack>
                            <Stack spacing={2}>
                                {group.students.map((student: any) => (
                                    <Stack key={student.id} direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar src={`https://i.pravatar.cc/150?u=${student.id}`} sx={{ width: 40, height: 40 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={700}>{student.firstName} {student.lastName}</Typography>
                                                <Typography variant="caption" color="text.secondary">{student.studentId}</Typography>
                                            </Box>
                                        </Stack>
                                        <IconButton
                                            size="small"
                                            sx={{ color: '#F9B43F' }}
                                            onClick={() => setAchievementDialog({
                                                open: true,
                                                studentId: student.id,
                                                studentName: `${student.firstName} ${student.lastName}`
                                            })}
                                        >
                                            <StarIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 3, borderRadius: '24px', bgcolor: '#263B7C', color: 'white' }}>
                            <Typography variant="subtitle1" fontWeight={800} mb={2}>Class Health</Typography>
                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">Avg. Attendance</Typography>
                                    <Typography fontWeight={700}>98%</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">Active Tasks</Typography>
                                    <Typography fontWeight={700}>{group.assignments.length}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>

            {/* Achievement Dialog */}
            <Dialog open={achievementDialog.open} onClose={() => setAchievementDialog({ ...achievementDialog, open: false })}>
                <DialogTitle sx={{ fontWeight: 800 }}>Award Badge to {achievementDialog.studentName}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            select
                            label="Badge Type"
                            fullWidth
                            value={achievementData.badgeType}
                            onChange={(e) => setAchievementData({ ...achievementData, badgeType: e.target.value })}
                        >
                            <MenuItem value="STAR_STUDENT">🌟 Star Student</MenuItem>
                            <MenuItem value="MOST_IMPROVED">📈 Most Improved</MenuItem>
                            <MenuItem value="EXCELLENT_WORK">🎯 Excellent Work</MenuItem>
                            <MenuItem value="GREAT_TEAMMATE">🤝 Great Teammate</MenuItem>
                            <MenuItem value="HELPING_HAND">🤝 Helping Hand</MenuItem>
                        </TextField>
                        <TextField
                            label="Teacher's Comment"
                            fullWidth
                            multiline
                            rows={3}
                            value={achievementData.comment}
                            onChange={(e) => setAchievementData({ ...achievementData, comment: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setAchievementDialog({ ...achievementDialog, open: false })}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#F9B43F', color: '#1a237e', fontWeight: 700 }}
                        onClick={async () => {
                            try {
                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                await axios.post(getApiUrl(`groups/${id}/achievements`), {
                                    studentId: achievementDialog.studentId,
                                    ...achievementData
                                }, { headers: { Authorization: `Bearer ${token}` } });
                                alert('Achievement awarded!');
                                setAchievementDialog({ ...achievementDialog, open: false });
                            } catch (error) {
                                console.error('Award achievement error:', error);
                            }
                        }}
                    >
                        Award Badge
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Students Dialog */}
            <Dialog open={manageStudentsDialog} onClose={() => setManageStudentsDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Manage Group Membership</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, mb: 3 }}>
                        <TextField
                            fullWidth
                            placeholder="Find students to add..."
                            value={studentSearch}
                            onChange={(e) => setStudentSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Box>
                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', borderRadius: '12px' }}>
                        <List>
                            {allStudents
                                .filter(s => 
                                    `${s.firstName} ${s.lastName}`.toLowerCase().includes(studentSearch.toLowerCase()) ||
                                    s.studentId.toLowerCase().includes(studentSearch.toLowerCase())
                                )
                                .map((student) => {
                                    const isSelected = selectedStudentIds.includes(student.id);
                                    return (
                                        <ListItem key={student.id} divider>
                                            <Avatar src={`https://i.pravatar.cc/150?u=${student.id}`} sx={{ mr: 2 }} />
                                            <ListItemText 
                                                primary={`${student.firstName} ${student.lastName}`} 
                                                secondary={`${student.studentId} - Grade ${student.grade}${student.class}`} 
                                            />
                                            <ListItemSecondaryAction>
                                                <Checkbox
                                                    edge="end"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedStudentIds([...selectedStudentIds, student.id]);
                                                        } else {
                                                            setSelectedStudentIds(selectedStudentIds.filter(id => id !== student.id));
                                                        }
                                                    }}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    );
                                })
                            }
                        </List>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setManageStudentsDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#263B7C', fontWeight: 700 }}
                        onClick={async () => {
                            try {
                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                // Calculate students to add and remove
                                const currentIds = group.students.map((s: any) => s.id);
                                const toAdd = selectedStudentIds.filter((id: string) => !currentIds.includes(id));
                                const toRemove = currentIds.filter((id: string) => !selectedStudentIds.includes(id));

                                if (toAdd.length > 0) {
                                    await axios.post(getApiUrl(`groups/${id}/students/add`), { studentIds: toAdd }, {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                }

                                if (toRemove.length > 0) {
                                    await axios.post(getApiUrl(`groups/${id}/students/remove`), { studentIds: toRemove }, {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                }

                                alert('Membership updated successfully!');
                                setManageStudentsDialog(false);
                                fetchGroupDetails();
                            } catch (error) {
                                console.error('Update membership error:', error);
                            }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Assignment Dialog */}
            <Dialog open={assignmentDialogOpen} onClose={() => setAssignmentDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Create New Assignment</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            label="Assignment Title"
                            fullWidth
                            value={newAssignment.title}
                            onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            value={newAssignment.description}
                            onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })}
                        />
                        <TextField
                            label="Due Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={newAssignment.dueDate}
                            onChange={(e) => setNewAssignment({ ...newAssignment, dueDate: e.target.value })}
                        />
                        <TextField
                            label="Attachment URL (Optional)"
                            fullWidth
                            value={newAssignment.attachmentUrl}
                            onChange={(e) => setNewAssignment({ ...newAssignment, attachmentUrl: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#263B7C', fontWeight: 700 }}
                        onClick={handleCreateAssignment}
                        disabled={!newAssignment.title || !newAssignment.dueDate}
                    >
                        Publish Assignment
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClassroomDetailPage;
